import { useCallback, useRef, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { borderRadius } from '../../tokens/spacing';
import { transitions, reducedMotionCSS } from '../../utils/motion';

type TabsVariant = 'underline' | 'pills';

export interface TabItem {
  key: string;
  label: string;
  disabled?: boolean;
  icon?: ReactNode;
}

export interface TabsProps {
  tabs: TabItem[];
  value: string;
  onChange: (key: string) => void;
  variant?: TabsVariant;
  className?: string;
  'aria-label'?: string;
}

export function Tabs({
  tabs,
  value,
  onChange,
  variant = 'underline',
  className,
  'aria-label': ariaLabel = 'Navegacao por abas',
}: TabsProps): JSX.Element {
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const currentTab = e.currentTarget as HTMLButtonElement;
      const tabButtons = Array.from(
        tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([disabled])') ?? [],
      );
      const currentIndex = tabButtons.indexOf(currentTab);

      let nextIndex: number | null = null;
      switch (e.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % tabButtons.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + tabButtons.length) % tabButtons.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabButtons.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      if (nextIndex !== null && tabButtons[nextIndex]) {
        tabButtons[nextIndex]!.focus();
        onChange(tabButtons[nextIndex]!.getAttribute('data-key') ?? '');
      }
    },
    [onChange],
  );

  const selectedTab = tabs.find((t) => t.key === value);

  const variantStyles: Record<
    TabsVariant,
    {
      tabList: React.CSSProperties;
      tab: (active: boolean) => React.CSSProperties;
      tabPanel: React.CSSProperties;
    }
  > = {
    underline: {
      tabList: { borderBottom: '1px solid #c4c6cf', gap: 0 },
      tab: (active) => ({
        padding: '0.625rem 1rem',
        border: 'none',
        background: 'none',
        color: active ? '#002045' : '#43474e',
        fontWeight: active ? 600 : 400,
        fontSize: '0.875rem',
        cursor: 'pointer',
        borderBottom: active ? '2px solid #002045' : '2px solid transparent',
        marginBottom: '-1px',
        transition: `${transitions.fast}`,
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
        transitionProperty: 'color, border-color',
      }),
      tabPanel: { padding: '1rem 0' },
    },
    pills: {
      tabList: { gap: '0.25rem' },
      tab: (active) => ({
        padding: '0.5rem 1rem',
        border: 'none',
        background: active ? '#002045' : '#f2f4f6',
        color: active ? '#ffffff' : '#43474e',
        fontWeight: active ? 600 : 400,
        fontSize: '0.875rem',
        cursor: 'pointer',
        borderRadius: borderRadius.sm,
        transition: `${transitions.fast}`,
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
        transitionProperty: 'background, color',
      }),
      tabPanel: { padding: '1rem 0' },
    },
  };

  const styles = variantStyles[variant];

  return (
    <div className={cn('ds-tabs', className)}>
      <div
        ref={tabListRef}
        role="tablist"
        aria-label={ariaLabel}
        style={{ display: 'flex', ...styles.tabList }}
      >
        {tabs.map((tab) => {
          const isActive = tab.key === value;
          return (
            <button
              key={tab.key}
              data-key={tab.key}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${tab.key}`}
              aria-disabled={tab.disabled}
              tabIndex={isActive ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => {
                if (!tab.disabled) onChange(tab.key);
              }}
              onKeyDown={handleKeyDown}
              style={styles.tab(isActive)}
              className={cn(
                'ds-tabs__tab',
                isActive && 'ds-tabs__tab--active',
                tab.disabled && 'ds-tabs__tab--disabled',
              )}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}>
                {tab.icon && <span style={{ fontSize: '1rem' }}>{tab.icon}</span>}
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {selectedTab && (
        <div
          id={`tabpanel-${selectedTab.key}`}
          role="tabpanel"
          aria-labelledby={`tab-${selectedTab.key}`}
          style={styles.tabPanel}
        />
      )}

      <style>{`
        .ds-tabs__tab:hover:not(:disabled):not(.ds-tabs__tab--disabled) {
          opacity: 0.85;
        }
        .ds-tabs__tab--disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .ds-tabs__tab:focus-visible {
          outline: 2px solid #002045;
          outline-offset: -2px;
          border-radius: 4px;
        }
        ${reducedMotionCSS('.ds-tabs__tab')}
      `}</style>
    </div>
  );
}

Tabs.displayName = 'Tabs';
